import React, { useState, useEffect, useRef } from 'react';
import { collection, query, getDocs, collectionGroup, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useAuth } from '../contexts/AuthContext';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboards = () => {
  const { currentUser } = useAuth();
  const [properties, setProperties] = useState([]);
  const [historyData, setHistoryData] = useState({});
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredData, setFilteredData] = useState({});
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [showPropertyFilter, setShowPropertyFilter] = useState(false);
  const propertyFilterRef = useRef(null);
  const [activeTab, setActiveTab] = useState('management'); // 'management' or 'divested'
  const [portfolioMetrics, setPortfolioMetrics] = useState({
    totalValue: 0,
    totalEquity: 0,
    totalNetIncome: 0,
    totalCashInvested: 0,
    aggregateROI: 0,
    aggregateCashOnCash: 0,
    lastMonthCashFlow: 0,
    lastMonthName: '',
    lastMonthYear: 0,
    propertyCount: 0,
    totalNetProfit: 0
  });

  // Fetch properties based on active tab
  useEffect(() => {
    const fetchProperties = async () => {
      if (!currentUser) return;

      try {
        const status = activeTab === 'management' ? 'UNDER_MANAGEMENT' : 'DIVESTED';
        
        const q = query(
          collection(db, 'properties'),
          where('userId', '==', currentUser.uid),
          where('status', '==', status)
        );
        const querySnapshot = await getDocs(q);

        const propertiesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setProperties(propertiesData);

        // After getting properties, fetch history data for each
        const historyPromises = propertiesData.map(property =>
          fetchHistoryForProperty(property.id)
        );

        const historyResults = await Promise.all(historyPromises);

        // Combine all history data
        const combinedHistory = {};

        historyResults.forEach((propertyHistory, index) => {
          const propertyId = propertiesData[index].id;

          propertyHistory.forEach(record => {
            const date = record.date;

            if (!combinedHistory[date]) {
              combinedHistory[date] = {
                date,
                totalROI: 0,
                cashOnCash: 0,
                netIncome: 0,
                currentValue: 0,
                count: 0,
                propertyData: []
              };
            }

            // Store individual property data for filtering
            combinedHistory[date].propertyData.push({
              propertyId,
              roi: Number(record.roi || 0),
              cashOnCash: Number(record.cashOnCash || 0),
              netIncome: Number(record.netIncome || 0),
              currentValue: Number(record.currentValue || 0)
            });

            combinedHistory[date].totalROI += Number(record.roi || 0);
            combinedHistory[date].cashOnCash += Number(record.cashOnCash || 0);
            combinedHistory[date].netIncome += Number(record.netIncome || 0);
            combinedHistory[date].currentValue += Number(record.currentValue || 0);
            combinedHistory[date].count += 1;
          });
        });

        // Calculate averages for percentage metrics
        Object.keys(combinedHistory).forEach(date => {
          const record = combinedHistory[date];
          const count = record.count;

          if (count > 0) {
            record.totalROI = record.totalROI / count;
            record.cashOnCash = record.cashOnCash / count;
          }
        });

        setHistoryData(combinedHistory);
        setFilteredData(combinedHistory);
        
        // Calculate portfolio metrics
        calculatePortfolioMetrics(propertiesData);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching properties:', error);
        setLoading(false);
      }
    };

    setLoading(true);
    fetchProperties();
  }, [currentUser, activeTab]);

  // Calculate portfolio metrics
  const calculatePortfolioMetrics = async (propertiesToCalculate) => {
    if (!propertiesToCalculate || propertiesToCalculate.length === 0) {
      setPortfolioMetrics({
        totalValue: 0,
        totalEquity: 0,
        totalNetIncome: 0,
        totalCashInvested: 0,
        aggregateROI: 0,
        aggregateCashOnCash: 0,
        lastMonthCashFlow: 0,
        lastMonthName: '',
        lastMonthYear: 0,
        propertyCount: 0,
        totalNetProfit: 0
      });
      return;
    }

    try {
      let totalValue = 0;
      let totalEquity = 0;
      let totalCashInvested = 0;
      let totalNetIncome = 0;
      let totalNetProfit = 0;

      // For divested properties, calculate total net profit
      if (activeTab === 'divested') {
        propertiesToCalculate.forEach(property => {
          const purchasePrice = property.purchasePrice || 0;
          const finalSalePrice = property.finalSalePrice || 0;
          const totalCashIn = property.totalCashInvested || 0;
          
          // Calculate profit from sale
          const saleProfit = finalSalePrice - purchasePrice;
          
          // Add to totals
          totalNetProfit += saleProfit;
          totalCashInvested += totalCashIn;
        });
      } else {
        // For under management properties
        propertiesToCalculate.forEach(property => {
          const currentValue = property.currentValue || 0;
          const mortgageBalance = property.mortgageBalance || 0;
          const equity = currentValue - mortgageBalance;
          const totalCashIn = property.totalCashInvested || 0;
          
          totalValue += currentValue;
          totalEquity += equity;
          totalCashInvested += totalCashIn;
        });
      }

      // Get transactions for last month
      const today = new Date();
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      
      const lastMonthName = lastMonth.toLocaleString('default', { month: 'long' });
      const lastMonthYear = lastMonth.getFullYear();

      let lastMonthCashFlow = 0;

      // Only calculate cash flow for under management properties
      if (activeTab === 'management') {
        // Get transactions for all properties
        const transactionsPromises = propertiesToCalculate.map(async (property) => {
          const q = query(
            collection(db, 'transactions'),
            where('propertyId', '==', property.id),
            where('date', '>=', lastMonth.toISOString().split('T')[0]),
            where('date', '<=', lastMonthEnd.toISOString().split('T')[0])
          );
          
          const querySnapshot = await getDocs(q);
          return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        });
        
        const allTransactions = await Promise.all(transactionsPromises);
        const flattenedTransactions = allTransactions.flat();
        
        // Calculate last month's cash flow
        flattenedTransactions.forEach(transaction => {
          if (transaction.type === 'income') {
            lastMonthCashFlow += Number(transaction.amount) || 0;
          } else {
            lastMonthCashFlow -= Number(transaction.amount) || 0;
          }
        });
        
        // Calculate all-time net income for under management properties
        const allTimeTransactionsPromises = propertiesToCalculate.map(async (property) => {
          const q = query(
            collection(db, 'transactions'),
            where('propertyId', '==', property.id)
          );
          
          const querySnapshot = await getDocs(q);
          return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        });
        
        const allTimeTransactions = await Promise.all(allTimeTransactionsPromises);
        const allTimeFlattenedTransactions = allTimeTransactions.flat();
        
        allTimeFlattenedTransactions.forEach(transaction => {
          if (transaction.type === 'income') {
            totalNetIncome += Number(transaction.amount) || 0;
          } else {
            totalNetIncome -= Number(transaction.amount) || 0;
          }
        });
      } else {
        // For divested properties, get all-time transactions to calculate net income
        const allTimeTransactionsPromises = propertiesToCalculate.map(async (property) => {
          const q = query(
            collection(db, 'transactions'),
            where('propertyId', '==', property.id)
          );
          
          const querySnapshot = await getDocs(q);
          return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        });
        
        const allTimeTransactions = await Promise.all(allTimeTransactionsPromises);
        const allTimeFlattenedTransactions = allTimeTransactions.flat();
        
        allTimeFlattenedTransactions.forEach(transaction => {
          if (transaction.type === 'income') {
            totalNetIncome += Number(transaction.amount) || 0;
          } else {
            totalNetIncome -= Number(transaction.amount) || 0;
          }
        });
        
        // For divested properties, add rental income/expenses to total profit
        totalNetProfit += totalNetIncome;
      }

      // Calculate ROI and Cash on Cash
      let aggregateROI = 0;
      let aggregateCashOnCash = 0;

      if (totalCashInvested > 0) {
        if (activeTab === 'management') {
          // For under management: ROI includes equity and cash flow
          aggregateROI = ((totalEquity + totalNetIncome) / totalCashInvested) * 100;
          // Cash on Cash is just the cash flow return
          aggregateCashOnCash = (totalNetIncome / totalCashInvested) * 100;
        } else {
          // For divested: ROI is the total profit percentage
          aggregateROI = (totalNetProfit / totalCashInvested) * 100;
        }
      }

      setPortfolioMetrics({
        totalValue,
        totalEquity,
        totalNetIncome,
        totalCashInvested,
        aggregateROI,
        aggregateCashOnCash,
        lastMonthCashFlow,
        lastMonthName,
        lastMonthYear,
        propertyCount: propertiesToCalculate.length,
        totalNetProfit
      });
    } catch (error) {
      console.error('Error calculating portfolio metrics:', error);
    }
  };

  // Fetch history data for a specific property
  const fetchHistoryForProperty = async (propertyId) => {
    try {
      const historyQuery = query(collection(db, `properties/${propertyId}/roiHistory`));
      const historySnapshot = await getDocs(historyQuery);

      return historySnapshot.docs.map(doc => ({
        id: doc.id,
        propertyId,
        ...doc.data()
      }));
    } catch (error) {
      console.error(`Error fetching history for property ${propertyId}:`, error);
      return [];
    }
  };

  // Apply date filters
  useEffect(() => {
    if (Object.keys(historyData).length === 0) return;

    // Start with all history data
    let filtered = { ...historyData };

    // Apply property filters if any are selected
    if (selectedProperties.length > 0) {
      // Filter history data to only include selected properties
      Object.keys(filtered).forEach(date => {
        const record = filtered[date];

        // Filter property-specific data
        const filteredPropertyData = record.propertyData.filter(
          pd => selectedProperties.includes(pd.propertyId)
        );

        if (filteredPropertyData.length === 0) {
          // If no properties match, remove this date from filtered data
          delete filtered[date];
        } else {
          // Recalculate aggregates based on filtered properties
          record.propertyData = filteredPropertyData;
          record.totalROI = filteredPropertyData.reduce((sum, pd) => sum + Number(pd.roi || 0), 0) / filteredPropertyData.length;
          record.cashOnCash = filteredPropertyData.reduce((sum, pd) => sum + Number(pd.cashOnCash || 0), 0) / filteredPropertyData.length;
          record.netIncome = filteredPropertyData.reduce((sum, pd) => sum + Number(pd.netIncome || 0), 0);
          record.currentValue = filteredPropertyData.reduce((sum, pd) => sum + Number(pd.currentValue || 0), 0);
          record.count = filteredPropertyData.length;
        }
      });
    }

    // Apply date filters
    if (startDate) {
      filtered = Object.fromEntries(
        Object.entries(filtered).filter(([date]) => date >= startDate)
      );
    }

    if (endDate) {
      // Set end date to end of day for inclusive filtering
      const endDateWithTime = new Date(endDate);
      endDateWithTime.setHours(23, 59, 59, 999);
      const formattedEndDate = endDateWithTime.toISOString().split('T')[0];

      filtered = Object.fromEntries(
        Object.entries(filtered).filter(([date]) => date <= formattedEndDate)
      );
    }

    setFilteredData(filtered);
  }, [historyData, startDate, endDate, selectedProperties]);

  // Handle date filter changes
  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDate') {
      setStartDate(value);
    } else if (name === 'endDate') {
      setEndDate(value);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedProperties([]);
  };

  // Toggle property selection
  const togglePropertySelection = (propertyId) => {
    setSelectedProperties(prev => {
      if (prev.includes(propertyId)) {
        return prev.filter(id => id !== propertyId);
      } else {
        return [...prev, propertyId];
      }
    });
  };

  // Select all properties
  const selectAllProperties = () => {
    setSelectedProperties(properties.map(property => property.id));
  };

  // Deselect all properties
  const deselectAllProperties = () => {
    setSelectedProperties([]);
  };

  // Toggle property filter dropdown
  const togglePropertyFilter = () => {
    setShowPropertyFilter(prev => !prev);
  };

  // Handle click outside to close property filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (propertyFilterRef.current && !propertyFilterRef.current.contains(event.target)) {
        setShowPropertyFilter(false);
      }
    };

    // Add event listener when dropdown is open
    if (showPropertyFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPropertyFilter]);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric'
    });
  };

  // Prepare chart data for Total ROI
  const prepareROIChartData = () => {
    const sortedDates = Object.keys(filteredData).sort();

    return {
      labels: sortedDates.map(date => formatDate(date)),
      datasets: [
        {
          label: 'Average Total ROI (%)',
          data: sortedDates.map(date => filteredData[date].totalROI),
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1
        }
      ]
    };
  };

  // Prepare chart data for Cash on Cash
  const prepareCoCChartData = () => {
    const sortedDates = Object.keys(filteredData).sort();

    return {
      labels: sortedDates.map(date => formatDate(date)),
      datasets: [
        {
          label: 'Average Cash on Cash (%)',
          data: sortedDates.map(date => filteredData[date].cashOnCash),
          borderColor: 'rgba(153, 102, 255, 1)',
          backgroundColor: 'rgba(153, 102, 255, 0.2)',
          tension: 0.1
        }
      ]
    };
  };

  // Prepare chart data for Net Income
  const prepareIncomeChartData = () => {
    const sortedDates = Object.keys(filteredData).sort();

    return {
      labels: sortedDates.map(date => formatDate(date)),
      datasets: [
        {
          label: 'Total Net Income ($)',
          data: sortedDates.map(date => filteredData[date].netIncome),
          borderColor: 'rgba(255, 159, 64, 1)',
          backgroundColor: 'rgba(255, 159, 64, 0.2)',
          tension: 0.1
        }
      ]
    };
  };

  // Prepare chart data for Current Value
  const prepareValueChartData = () => {
    const sortedDates = Object.keys(filteredData).sort();

    return {
      labels: sortedDates.map(date => formatDate(date)),
      datasets: [
        {
          label: 'Total Property Value ($)',
          data: sortedDates.map(date => filteredData[date].currentValue),
          borderColor: 'rgba(54, 162, 235, 1)',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          tension: 0.1
        }
      ]
    };
  };

  // Chart options
  const getChartOptions = (title, yAxisLabel) => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: 'white'
          }
        },
        title: {
          display: true,
          text: title,
          color: 'white',
          font: {
            size: 16
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false
        }
      },
      scales: {
        x: {
          ticks: {
            color: 'white'
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        },
        y: {
          ticks: {
            color: 'white'
          },
          grid: {
            color: 'rgba(255, 255, 255, 0.1)'
          },
          title: {
            display: true,
            text: yAxisLabel,
            color: 'white'
          }
        }
      }
    };
  };

  // Custom tooltip component for portfolio summary
  const TooltipIcon = ({ text }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    
    return (
      <div 
        className="ms-1 text-muted position-relative d-inline-block" 
        style={{ cursor: 'help' }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span 
          className="d-inline-flex justify-content-center align-items-center rounded-circle bg-secondary text-white" 
          style={{ 
            width: '14px', 
            height: '14px', 
            fontSize: '10px',
            fontWeight: 'bold'
          }}
        >
          i
        </span>
        {showTooltip && (
          <div 
            className="position-absolute bg-dark text-white p-2 rounded shadow-sm" 
            style={{ 
              width: '200px', 
              zIndex: 1000, 
              bottom: '100%', 
              left: '50%', 
              transform: 'translateX(-50%)',
              fontSize: '0.8rem',
              border: '1px solid #6c757d'
            }}
          >
            {text}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container-fluid py-4">
      <div className="row mb-2">
        <div className="col">
          <h2 className="mb-2">Portfolio Dashboards</h2>
          
          {/* Dashboard Tabs */}
          <div className="mb-0 tab-container">
            <ul className="nav nav-tabs border-0">
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'management' ? 'active' : 'text-secondary'}`}
                  onClick={() => setActiveTab('management')}
                >
                  <i className="bi bi-house-door me-2"></i> Under Management
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeTab === 'divested' ? 'active' : 'text-secondary'}`}
                  onClick={() => setActiveTab('divested')}
                >
                  <i className="bi bi-graph-up me-2"></i> Sold Properties
                </button>
              </li>
            </ul>
            <div className="tab-divider"></div>
          </div>
          
          <p className="text-muted">
            {activeTab === 'management' 
              ? ''
              : ''
            }
          </p>
        </div>
      </div>

      {/* Portfolio Summary Card */}
      {!loading && portfolioMetrics.propertyCount > 0 && (
        <div className="card bg-dark text-white border-secondary mb-4">
          <div className="card-body">
            <h5 className="card-title">Portfolio Summary</h5>
            <div className="row">
              {activeTab === 'management' ? (
                // Under Management Metrics
                <>
                  <div className="col">
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center">
                        <span className="text-muted small">Total Portfolio Value</span>
                        <TooltipIcon text={`Total value of ${portfolioMetrics.propertyCount} properties under management`} />
                      </div>
                      <span className="fs-4">${portfolioMetrics.totalValue.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="col">
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center">
                        <span className="text-muted small">Total Equity</span>
                        <TooltipIcon text="Your ownership stake across all properties" />
                      </div>
                      <span className="fs-4">${portfolioMetrics.totalEquity.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="col">
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center">
                        <span className="text-muted small">Aggregate ROI</span>
                        <TooltipIcon text="Return on investment across all properties (includes cash flow and equity gains)" />
                      </div>
                      <span className="fs-4">{portfolioMetrics.aggregateROI.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="col">
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center">
                        <span className="text-muted small">Cash on Cash</span>
                        <TooltipIcon text="Cash on cash return across all properties (cash flow only)" />
                      </div>
                      <span className="fs-4">{portfolioMetrics.aggregateCashOnCash.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="col">
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center">
                        <span className="text-muted small">Properties Owned</span>
                        <TooltipIcon text="Total number of properties under management" />
                      </div>
                      <span className="fs-4">{portfolioMetrics.propertyCount}</span>
                    </div>
                  </div>
                </>
              ) : (
                // Divested Properties Metrics
                <>
                  <div className="col-md-4">
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center">
                        <span className="text-muted small">Total Net Profit</span>
                        <TooltipIcon text={`Total profit from ${portfolioMetrics.propertyCount} divested properties (sale profit + rental income)`} />
                      </div>
                      <span className="fs-4">${portfolioMetrics.totalNetProfit.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center">
                        <span className="text-muted small">Total ROI</span>
                        <TooltipIcon text="Return on investment across all divested properties" />
                      </div>
                      <span className="fs-4">{portfolioMetrics.aggregateROI.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center">
                        <span className="text-muted small">Properties Sold</span>
                        <TooltipIcon text="Total number of properties you've divested" />
                      </div>
                      <span className="fs-4">{portfolioMetrics.propertyCount}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter Controls - Only show for Under Management tab with properties */}
      {activeTab === 'management' && properties.length > 0 && (
        <div className="mb-4">
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
              <label htmlFor="startDate" className="form-label text-white mb-1">Start Date</label>
              <input
                type="date"
                className="form-control bg-dark text-white border-secondary date-input-white-icon"
                id="startDate"
                name="startDate"
                value={startDate}
                onChange={handleDateFilterChange}
              />
            </div>
            <div>
              <label htmlFor="endDate" className="form-label text-white mb-1">End Date</label>
              <input
                type="date"
                className="form-control bg-dark text-white border-secondary date-input-white-icon"
                id="endDate"
                name="endDate"
                value={endDate}
                onChange={handleDateFilterChange}
              />
            </div>

            {/* Property Filter Dropdown */}
            <div>
              <label htmlFor="propertyFilter" className="form-label text-white mb-1">Properties</label>
              <div className="dropdown" ref={propertyFilterRef}>
                <button
                  id="propertyFilter"
                  className="form-control bg-dark text-white border-secondary d-flex justify-content-between align-items-center"
                  type="button"
                  onClick={togglePropertyFilter}
                  aria-expanded={showPropertyFilter}
                  style={{ minWidth: '200px' }}
                >
                  <span>
                    {selectedProperties.length > 0
                      ? `${selectedProperties.length} Properties Selected`
                      : 'All Properties'}
                  </span>
                  <i className="bi bi-chevron-down"></i>
                </button>

                {showPropertyFilter && (
                  <div className="dropdown-menu dropdown-menu-dark border-secondary show p-2" style={{ minWidth: '250px' }}>
                    <div className="d-flex justify-content-between mb-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={selectAllProperties}
                      >
                        Select All
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={deselectAllProperties}
                      >
                        Deselect All
                      </button>
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {properties.map(property => (
                        <div className="form-check" key={property.id}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`property-${property.id}`}
                            checked={selectedProperties.includes(property.id)}
                            onChange={() => togglePropertySelection(property.id)}
                          />
                          <label className="form-check-label" htmlFor={`property-${property.id}`}>
                            {property.address?.street || 'Unnamed Property'}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(startDate || endDate || selectedProperties.length > 0) && (
              <div className="d-flex align-items-end">
                <button
                  className="btn btn-outline-secondary"
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              </div>
            )}
            {Object.keys(filteredData).length === 0 && Object.keys(historyData).length > 0 && (
              <div className="ms-auto">
                <div className="text-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  No data found for the selected filters
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Charts */}
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading portfolio data...</p>
        </div>
      ) : (
        <>
          {activeTab === 'management' && Object.keys(filteredData).length > 0 ? (
            <div className="row g-4">
              {/* ROI Chart */}
              <div className="col-md-6">
                <div className="card bg-dark border-secondary h-100">
                  <div className="card-body">
                    <div style={{ height: '300px' }}>
                      <Line
                        data={prepareROIChartData()}
                        options={getChartOptions('Total ROI Over Time', 'ROI (%)')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Cash on Cash Chart */}
              <div className="col-md-6">
                <div className="card bg-dark border-secondary h-100">
                  <div className="card-body">
                    <div style={{ height: '300px' }}>
                      <Line
                        data={prepareCoCChartData()}
                        options={getChartOptions('Cash on Cash Return Over Time', 'Cash on Cash (%)')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Income Chart */}
              <div className="col-md-6">
                <div className="card bg-dark border-secondary h-100">
                  <div className="card-body">
                    <div style={{ height: '300px' }}>
                      <Line
                        data={prepareIncomeChartData()}
                        options={getChartOptions('Net Income Over Time', 'Net Income ($)')}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Property Value Chart */}
              <div className="col-md-6">
                <div className="card bg-dark border-secondary h-100">
                  <div className="card-body">
                    <div style={{ height: '300px' }}>
                      <Line
                        data={prepareValueChartData()}
                        options={getChartOptions('Property Value Over Time', 'Value ($)')}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* Property Cards Section */}
      {!loading && (
        <div className="mt-5">
          {properties.length > 0 ? (
            <>
              <h4 className="mb-3">{activeTab === 'management' ? 'Properties Under Management' : 'Sold Properties'}</h4>
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4">
                {properties.map(property => {
                  // Find the most recent history data for this property
                  const propertyHistory = Object.keys(historyData)
                    .sort((a, b) => new Date(b) - new Date(a)) // Sort dates in descending order
                    .map(date => historyData[date].propertyData.find(pd => pd.propertyId === property.id))
                    .filter(Boolean)[0] || { roi: 0, cashOnCash: 0 };

                  // Calculate equity (currentValue - mortgageBalance)
                  const currentValue = property.currentValue || 0;
                  const mortgageBalance = property.mortgageBalance || 0;
                  const equity = Math.max(0, currentValue - mortgageBalance);

                  return (
                    <div className="col" key={property.id}>
                      <div 
                        className="card bg-dark border-secondary h-100 property-card hover-effect" 
                        onClick={() => window.location.href = `/property/${property.id}`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="card-body p-3">
                          <h5 className="card-title text-light mb-2">
                            {property.address?.street || 'Unnamed Property'}
                          </h5>
                          <p className="card-text text-muted mb-3">
                            {[
                              property.address?.city,
                              property.address?.state,
                              property.address?.zipCode
                            ].filter(Boolean).join(', ')}
                          </p>
                          
                          {activeTab === 'management' ? (
                            // Under Management Property Metrics
                            <>
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Equity:</span>
                                <span className="fw-semibold text-light">
                                  ${equity.toLocaleString()}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">ROI:</span>
                                <span 
                                  className={`fw-semibold ${
                                    propertyHistory.roi > 0 
                                      ? 'text-success-light' 
                                      : propertyHistory.roi < 0 
                                        ? 'text-danger' 
                                        : 'text-light'
                                  }`}
                                >
                                  {propertyHistory.roi.toFixed(2)}%
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Cash on Cash:</span>
                                <span 
                                  className={`fw-semibold ${
                                    propertyHistory.cashOnCash > 0 
                                      ? 'text-success-light' 
                                      : propertyHistory.cashOnCash < 0 
                                        ? 'text-danger' 
                                        : 'text-light'
                                  }`}
                                >
                                  {propertyHistory.cashOnCash.toFixed(2)}%
                                </span>
                              </div>
                            </>
                          ) : (
                            // Sold Properties Metrics
                            <>
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Net Profit:</span>
                                <span className="fw-semibold text-light">
                                  ${((property.finalSalePrice || 0) - (property.purchasePrice || 0) + (property.netIncome || 0)).toLocaleString()}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Total Return:</span>
                                <span 
                                  className={`fw-semibold ${
                                    property.totalCashInvested > 0 
                                      ? ((property.finalSalePrice - property.purchasePrice + (property.netIncome || 0)) / property.totalCashInvested * 100) > 0
                                        ? 'text-success-light'
                                        : 'text-danger'
                                      : 'text-light'
                                  }`}
                                >
                                  {property.totalCashInvested > 0 
                                    ? ((property.finalSalePrice - property.purchasePrice + (property.netIncome || 0)) / property.totalCashInvested * 100).toFixed(2)
                                    : '0.00'}%
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-white text-center mt-5">
              <h5 className="mb-3">{activeTab === 'management' ? 'No Properties Under Management' : 'No Sold Properties'}</h5>
              <p>
                {activeTab === 'management' 
                  ? 'You currently have no properties under management. Add a property to see portfolio metrics and performance graphs.'
                  : 'You currently have no sold properties. When you mark a property as divested, it will appear here.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* CSS for white date picker icon and custom colors */}
      <style jsx="true">{`
        .date-input-white-icon::-webkit-calendar-picker-indicator {
          filter: invert(1);
        }
        
        .text-success-light {
          color: #8eff8e !important;
        }

        /* Custom tab styling */
        .nav-tabs {
          border-bottom: none;
        }
        
        .nav-tabs .nav-link {
          border: none;
          border-radius: 0;
          padding: 0.5rem 1rem;
          transition: all 0.2s ease;
          font-weight: 500;
          color: #6c757d;
        }
        
        .nav-tabs .nav-link:not(.active) {
          background-color: transparent;
          box-shadow: none;
        }
        
        .nav-tabs .nav-link.active {
          border: none;
          border-bottom: 3px solid white;
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
          background-color: transparent !important;
          color: white;
          font-weight: 600;
        }
        
        .tab-divider {
          width: 100%;
          height: 1px;
          background-color: #6c757d;
          margin-top: 0;
        }
      `}</style>
    </div>
  );
};

export default Dashboards;
